import { ITransfer } from "types/MoneyTransfers";
import { IOrder } from "types/Order/Order";

export const mapFormToRequestBody = ({
  orderId,
  moneyTransfers,
  note,
}: {
  orderId: string;
  moneyTransfers: ITransfer[];
  note?: string;
}) => {
  return {
    orderId,
    note,
    moneyTransfers: moneyTransfers
      .filter(
        (transfer) =>
          transfer.selectedPartyToTransferFrom &&
          transfer.selectedPartyToTransferTo &&
          transfer.selectedAmountToTransfer > 0
      )
      .map((transfer) => ({
        fromProviderId: transfer?.selectedPartyToTransferFrom?.id,
        toProviderId: transfer?.selectedPartyToTransferTo?.id,
        amountInCents: transfer.selectedAmountToTransfer,
        withholdClearFee: transfer.withholdClearFee,
        // CLE-2458: only send the chosen refund card when the destination is the patient (a refund).
        ...(transfer.selectedPartyToTransferTo?.type === "patient" &&
        transfer.selectedRefundCardPaymentId
          ? { sourcePaymentId: transfer.selectedRefundCardPaymentId }
          : {}),
      })),
  };
};

// CLE-2473 / CLE-2863: a Finix transfer row with `returnedAt` set never moved money — an ACH that
// came back RETURNED, or a reversal (patient refund) Finix accepted and then declined. It must not
// count toward what a provider holds. The backend's getFinixTransfersStateByProviderId skips these
// rows the same way, so the dialog's caps match what the server will allow.
const finixTransferMoved = (transfer: { returnedAt?: string | null }) =>
  !transfer.returnedAt;

export interface IProviderTotal {
  name: string;
  id: string;
  totalAmount: number;
  type: string;
}

/**
 * Per-provider amount the "From" dropdown offers and caps the amount at: what each provider still
 * holds on this order (transfers in, net of reversals out, plus the Clear fee withheld).
 */
export const buildProviderTotals = ({
  providerStripeTransfers,
  providerFinixTransfers,
}: {
  providerStripeTransfers?: IOrder["providerStripeTransfers"];
  providerFinixTransfers?: IOrder["providerFinixTransfers"];
}): IProviderTotal[] => {
  const totals: { [key: string]: IProviderTotal } = {};
  // Aggregate from Stripe transfers
  providerStripeTransfers?.forEach((transfer) => {
    if (!totals[transfer.providerId]) {
      totals[transfer.providerId] = {
        name: transfer.provider.name,
        id: transfer.providerId,
        totalAmount: 0,
        type: "provider",
      };
    }
    totals[transfer.providerId].totalAmount +=
      transfer.amountInCents + transfer.clearFeeAmountInCents;
  });
  // Aggregate from Finix transfers. Skip CLEARHEALTH_UPCHARGE rows — those have a
  // null providerId/provider and represent the Clear Health merchant's split, not a
  // refundable provider pot. Backend mirrors this in getFinixTransfersStateByProviderId.
  providerFinixTransfers?.forEach((transfer: any) => {
    if (!transfer.providerId || !transfer.provider) return;
    if (!finixTransferMoved(transfer)) return;
    if (!totals[transfer.providerId]) {
      totals[transfer.providerId] = {
        name: transfer.provider.name,
        id: transfer.providerId,
        totalAmount: 0,
        type: "provider",
      };
    }
    totals[transfer.providerId].totalAmount +=
      transfer.amountInCents + (transfer.clearFeeAmountInCents || 0);
  });
  return Object.values(totals);
};

// CLE-2458: a patient refund reverses the chosen provider's transfers, and a split-paid card's
// money is spread across providers. So the real cap for "refund $X to card C from provider P" is
// how much of card C's money provider P still holds — NOT card C's full charge. We compute that
// (provider ∩ card) reversible from the order's provider transfers; reversal rows are negative and
// carry the same providerId+paymentId, so summing nets out anything already refunded. The backend
// enforces the same limit before any reversal, so this is a UX guard, not the source of truth.
export const reversibleForProviderCardInCents = ({
  order,
  providerId,
  cardPaymentId,
}: {
  order: IOrder;
  providerId?: string | null;
  cardPaymentId?: string;
}): number | undefined => {
  if (!providerId || !cardPaymentId) return undefined;
  const finixRows: any[] = (order.providerFinixTransfers || []).filter(
    (t: any) =>
      t.providerId === providerId &&
      t.paymentId === cardPaymentId &&
      finixTransferMoved(t)
  );
  const stripeRows: any[] = (order.providerStripeTransfers || []).filter(
    (t: any) => t.providerId === providerId && t.paymentId === cardPaymentId
  );
  const rows: any[] = finixRows.length ? finixRows : stripeRows;
  const net = rows.reduce(
    (sum: number, t: any) =>
      sum + t.amountInCents + (t.clearFeeAmountInCents || 0),
    0
  );
  return Math.max(0, net);
};
